import { notifyError } from "@/lib/notify-error";
import { useEffect, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { AlertCircle, Columns, FileSpreadsheet, Loader2, Plus, Save, Trash2, Undo2, X } from "lucide-react";
import { toast } from "sonner";

import {
  EditableDataGrid,
  type EditableDataGridHandle,
} from "@/components/editable-data-grid";
import {
  SpecPilotSourcePdfPanel,
  type SourcePdfRef,
} from "@/components/spec-pilot-source-pdf-panel";
import { uploadTableRowsPayload } from "@/lib/table-rows-upload";

import { UnsavedChangesDialog } from "@/components/unsaved-changes-dialog";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

// ============================================================================
// Types
// ============================================================================

export interface TableEditorModalProps {
  table: {
    _id: Id<"specPilotExtractedTables">;
    name?: string;
    headers: string[];
    rows: string[][];
    pageNumber: number;
    extractionMethod?: string;
    selectedForSubmittal?: boolean;
  };
  isOpen: boolean;
  onClose: () => void;
  sourcePdf?: SourcePdfRef | null;
  sourceId?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Full-screen editor modal for a hydrated SpecPilot extracted table.
 *
 * @param props - {@link TableEditorModalProps}
 * @param props.table - Hydrated extracted table data, including the Convex table
 *   ID, editable `headers`/`rows`, source `pageNumber`, and optional display
 *   metadata (`name`, `extractionMethod`, `selectedForSubmittal`).
 * @param props.isOpen - Controls whether the dialog is visible. When false, the
 *   modal is closed and its content is hidden by the dialog component.
 * @param props.onClose - Called when the user dismisses the modal or when the
 *   dialog open state changes to false.
 * @param props.sourcePdf - Optional signed source PDF reference for the side-by-side
 *   viewer. Pass `null` when unavailable; `fileUrl` is expected to be a browser
 *   fetchable URL and `filename` is used for viewer labeling/media detection.
 * @param props.sourceId - Optional stable source identifier used as the document
 *   viewport revision key. When omitted, the PDF panel is not rendered.
 */
export function TableEditorModal({
  table,
  isOpen,
  onClose,
  sourcePdf,
  sourceId,
}: TableEditorModalProps) {
  const [hasError, setHasError] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [canUndoRowAction, setCanUndoRowAction] = useState(false);
  const [showUnsavedCloseWarning, setShowUnsavedCloseWarning] = useState(false);
  useOnEditAccessLost(() => setShowUnsavedCloseWarning(false));
  const [columnHeaders, setColumnHeaders] = useState(table.headers);
  const gridRef = useRef<EditableDataGridHandle>(null);

  useEffect(() => {
    setColumnHeaders(table.headers);
  }, [table.headers]);

  useEffect(() => {
    if (!isOpen) {
      setShowUnsavedCloseWarning(false);
    }
  }, [isOpen]);
  
  // Table edits are project writes; reviewers get a view-only grid.
  const canEdit = useCanEdit();
  // Mutation for updating table data
  const updateTableData = useMutation(api.specPilot.extractedTables.updateData);
  const generateR2UploadUrl = useMutation(api.files.generateR2UploadUrl);

  // Handle save
  const handleSave = async (
    headers: string[],
    rows: string[][],
    metadata?: { deletedRowIndices: number[] }
  ) => {
    try {
      const uploadedRows = await uploadTableRowsPayload({
        generateUploadUrl: async () =>
          await generateR2UploadUrl({
            prefix: "spec-pilot/rows",
            filename: `${table._id}-rows.json.gz`,
          }),
        headers,
        rows,
      });

      await updateTableData({
        tableId: table._id,
        headers,
        rowsStorageKey: uploadedRows.rowsStorageKey,
        rowsFormat: uploadedRows.rowsFormat,
        rowCount: uploadedRows.rowCount,
        deletedRowIndices: metadata?.deletedRowIndices ?? [],
      });
      toast.success("Table saved successfully");
      setHasError(false);
    } catch (error) {
      notifyError(error, "Failed to save table");
      console.error("Error saving table:", error);
      setHasError(true);
      throw error;
    }
  };

  const handleAddColumn = () => {
    const nextHeader = `Column ${columnHeaders.length + 1}`;
    gridRef.current?.addColumn();
    setColumnHeaders((prev) => [...prev, nextHeader]);
  };

  const handleDeleteColumn = (index: number) => {
    gridRef.current?.deleteColumn(index);
    setColumnHeaders((prev) => prev.filter((_, colIndex) => colIndex !== index));
  };

  const handleCancel = () => {
    gridRef.current?.cancel();
    setColumnHeaders(table.headers);
  };

  const requestClose = () => {
    if (hasChanges) {
      setShowUnsavedCloseWarning(true);
      return;
    }
    onClose();
  };

  const handleDiscardAndClose = () => {
    handleCancel();
    setShowUnsavedCloseWarning(false);
    onClose();
  };

  const handleSaveAndClose = async () => {
    try {
      await gridRef.current?.save();
      setShowUnsavedCloseWarning(false);
      onClose();
    } catch {
      // Error toast + hasError state already handled in handleSave.
    }
  };

  const tableName = table.name || "Untitled Table";
  const rowCount = table.rows.length;
  const colCount = columnHeaders.length;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && requestClose()}>
        <DialogContent className="h-[92dvh] w-[98vw] max-w-[1760px]! gap-0 overflow-hidden p-0 flex flex-col" showCloseButton={false}>
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <DialogTitle className="flex items-center gap-2 text-lg truncate">
                  <FileSpreadsheet className="size-5 shrink-0" />
                  <span className="truncate">{tableName}</span>
                </DialogTitle>
                <DialogDescription className="mt-1.5 text-sm">
                  Page {table.pageNumber} • {rowCount} rows × {colCount} columns
                </DialogDescription>
              </div>
              {canEdit && (
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => gridRef.current?.addRow()}
                    disabled={isSaving}
                  >
                    <Plus className="size-4" />
                    Add Row
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="outline" size="sm" disabled={isSaving}>
                        <Columns className="size-4" />
                        Columns
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onSelect={handleAddColumn}>
                        <Plus />
                        Add Column
                      </DropdownMenuItem>
                      <DropdownMenuItem disabled className="text-xs font-medium text-muted-foreground">
                        Delete column
                      </DropdownMenuItem>
                      {columnHeaders.map((header, index) => (
                        <DropdownMenuItem
                          key={`${header}-${index}`}
                          variant="destructive"
                          disabled={columnHeaders.length <= 1}
                          onSelect={() => handleDeleteColumn(index)}
                        >
                          <Trash2 />
                          {header || `Column ${index + 1}`}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => gridRef.current?.undoRowAction()}
                    disabled={isSaving || !canUndoRowAction}
                  >
                    <Undo2 className="size-4" />
                    Undo Row Action
                  </Button>
                  {hasChanges ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCancel}
                      disabled={isSaving}
                    >
                      <X className="size-4" />
                      Cancel
                    </Button>
                  ) : null}
                  {hasChanges ? (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        void gridRef.current?.save().catch(() => {
                          // Error toast + hasError state already handled in handleSave.
                        });
                      }}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Save className="size-4" />
                      )}
                      {isSaving ? "Saving..." : "Save Changes"}
                    </Button>
                  ) : null}
                </div>
              )}
              <IconButton
                label="Close"
                icon={X}
                size="md"
                className="shrink-0 -me-2"
                onClick={requestClose}
              />
            </div>

            {hasError && (
              <Alert variant="destructive" className="mt-3">
                <AlertCircle className="size-4" />
                <AlertDescription>
                  Failed to save changes. Please try again.
                </AlertDescription>
              </Alert>
            )}
          </DialogHeader>

          {/*
            Two-column at lg: the content area is height-contained (min-h-0 +
            overflow-hidden) so the table scrolls inside its own column and the
            PDF panel stays pinned. Below lg the columns stack, so the content
            area itself becomes the single scroller.
          */}
          <div className="grid flex-1 gap-4 overflow-auto p-4 lg:min-h-0 lg:grid-cols-[minmax(0,1fr)_minmax(380px,40%)] lg:overflow-hidden lg:p-6">
            <div className="min-w-0 lg:flex lg:min-h-0 lg:flex-col">
              <EditableDataGrid
                ref={gridRef}
                headers={table.headers}
                rows={table.rows}
                readOnly={!canEdit}
                onSave={handleSave}
                hideActionBar
                className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col"
                scrollAreaClassName="max-h-[unset] lg:min-h-0 lg:flex-1"
                onHasChangesChange={setHasChanges}
                onIsSavingChange={setIsSaving}
                onCanUndoRowActionChange={setCanUndoRowAction}
              />
            </div>
            {sourceId ? (
              <SpecPilotSourcePdfPanel
                sourcePdf={sourcePdf}
                sourceId={sourceId}
                pageNumber={table.pageNumber}
                className="h-[55dvh] min-h-[360px] lg:h-full lg:min-h-0"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={showUnsavedCloseWarning}
        onOpenChange={setShowUnsavedCloseWarning}
        onSave={handleSaveAndClose}
        onDiscard={handleDiscardAndClose}
        isSaving={isSaving}
      />
    </>
  );
}

/*
 * Usage Example:
 *
 * import { TableEditorModal } from "@/components/table-editor-modal";
 *
 * function ExtractedTableCard({ table }) {
 *   const [isModalOpen, setIsModalOpen] = useState(false);
 *
 *   return (
 *     <>
 *       <Card>
 *         <Button onClick={() => setIsModalOpen(true)}>
 *           View Full Table
 *         </Button>
 *       </Card>
 *
 *       <TableEditorModal
 *         table={table}
 *         isOpen={isModalOpen}
 *         onClose={() => setIsModalOpen(false)}
 *       />
 *     </>
 *   );
 * }
 *
 * Accessibility Checklist:
 * - Dialog has proper ARIA attributes via shadcn/ui Dialog component
 * - Close button is keyboard accessible with visible focus state
 * - Error alerts are announced to screen readers
 * - Content area is scrollable for large tables
 *
 * Performance Considerations:
 * - Uses EditableDataGrid which manages local state to minimize re-renders
 * - Modal content only renders when opened
 * - Save operations are batched (not per-keystroke)
 */
